import { apiRequest } from "@/lib/queryClient";
import { ArticleExplanation, Filter, FinanceMonitorResponse, MarketBriefResponse, MonitorBootstrap, NewsItem, NewsRange, NewsWorkspaceItem, RefreshInterval, SignalsResponse } from "@/lib/types";
import { SECTOR_PROVIDERS } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { getVaultEntry, getVaultKey } from "./useKeyVault";
import type { WhoResearchContext } from "@shared/intelligenceReadiness";
import type { IdeasHttpResponse } from "@shared/ideasRetention";
import { getClientIdentityHeaders } from "@/lib/clientIdentity";

// Hook for fetching all news
export function useAllNews(limit: number = 1000) {
  const url = `/api/news?limit=${limit}`;
  return useQuery<NewsItem[]>({
    queryKey: [url],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch news");
      return res.json();
    },
    staleTime: 30 * 1000,        // 30 seconds
    refetchInterval: 60 * 1000, // Poll every 60 seconds
    refetchOnWindowFocus: true,
  });
}

export function useMonitorBootstrap() {
  return useQuery<MonitorBootstrap & { intelligenceContext?: WhoResearchContext }>({
    queryKey: ["/api/monitor/bootstrap"],
    queryFn: async () => {
      const res = await fetch("/api/monitor/bootstrap", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch monitor bootstrap");
      return res.json();
    },
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useFinanceMonitor() {
  return useQuery<FinanceMonitorResponse>({
    queryKey: ["/api/monitor/finance"],
    queryFn: async () => {
      const headers: Record<string, string> = {};
      const mappings = [
        ["fmp", "x-fmp-key"],
        ["fred", "x-fred-key"],
        ["alphaVantage", "x-alpha-vantage-key"],
        ["eia", "x-eia-key"],
        ["cmc", "x-cmc-key"],
        ["newsapi", "x-newsapi-key"],
      ] as const;
      for (const [provider, header] of mappings) {
        const value = getVaultKey(provider);
        if (value) headers[header] = value;
      }
      const res = await fetch("/api/monitor/finance", { credentials: "include", headers });
      if (!res.ok) throw new Error("Failed to fetch finance monitor");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useMarketBrief() {
  return useQuery<MarketBriefResponse>({
    queryKey: ["/api/brief"],
    queryFn: async ({ signal }) => {
      const res = await fetch("/api/brief", { credentials: "include", signal });
      if (!res.ok) throw new Error("Failed to fetch market brief");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useNytTopStories(enabled = true) {
  return useQuery<{ nyt: { status: string; items: NewsWorkspaceItem[]; message?: string }; attribution: string }>({
    queryKey: ["/api/news/workspace/live"],
    queryFn: async () => {
      const res = await fetch("/api/news/workspace/live", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch NYT Top Stories");
      return res.json();
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
    enabled,
  });
}

export function useNytPopular(period: 1 | 7 | 30, enabled = true) {
  return useQuery<{ nyt: { status: string; items: NewsWorkspaceItem[]; message?: string }; attribution: string }>({
    queryKey: ["/api/news/workspace/popular", period],
    queryFn: async () => {
      const res = await fetch(`/api/news/workspace/popular?period=${period}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch NYT Popular");
      return res.json();
    },
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useWorkspaceSearch() {
  return useMutation<{ current: { status: string; items: NewsWorkspaceItem[] }; nyt: { status: string; items: NewsWorkspaceItem[]; message?: string }; attribution: string }, Error, { query: string; range: NewsRange }>({
    mutationFn: async (request) => {
      const res = await fetch("/api/news/workspace/search", { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(request) });
      if (!res.ok) throw new Error("News search failed");
      return res.json();
    },
  });
}

export function useExplainArticle() {
  return useMutation<{ item: NewsWorkspaceItem; explanation: ArticleExplanation }, Error, NewsWorkspaceItem>({
    mutationFn: async (item) => {
      const res = await fetch("/api/news/workspace/explain", { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ item }) });
      if (!res.ok) throw new Error("Explanation failed");
      return res.json();
    },
  });
}

export function useBriefSearch() {
  return useMutation<any, Error, { query: string; range: NewsRange }>({
    mutationFn: async (request) => {
      const res = await fetch("/api/brief/search", { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(request) });
      if (!res.ok) throw new Error("Brief search failed");
      return res.json();
    },
  });
}

// Hook for fetching top ranked news
export function useTopNews(limit: number = 10) {
  const url = `/api/news/top?limit=${limit}`;
  return useQuery<NewsItem[]>({
    queryKey: [url],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch top news");
      return res.json();
    },
    staleTime: 4 * 60 * 1000, // Consider data fresh for 4 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

// Hook for fetching filtered news with pagination
export function useFilteredNews(filter: Filter, currentPage: number = 1, pageSize: number = 50) {
  const queryParams = new URLSearchParams();

  // Sector filter is client-side only — translate to provider list
  const sectorProviders = filter.sector ? SECTOR_PROVIDERS[filter.sector] : null;

  if (filter.provider) {
    queryParams.append('provider', filter.provider);
  } else if (!filter.sector && filter.region) {
    queryParams.append('region', filter.region);
  }

  queryParams.append('limit', '1000');

  return useQuery<NewsItem[]>({
    queryKey: ["useFilteredNews", filter, currentPage, pageSize],
    queryFn: async () => {
      const url = `/api/news/filter?${queryParams.toString()}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch filtered news");
      let data: NewsItem[] = await res.json();

      // Client-side sector filtering
      if (sectorProviders) {
        data = data.filter(item => sectorProviders.includes(item.provider.toUpperCase()));
      }

      // Client-side region filtering when sector is also active
      if (filter.sector && filter.region) {
        data = data.filter(item => item.region === filter.region);
      }

      return data;
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

// Hook for fetching a specific news item
export function useNewsItem(id: number | null) {
  return useQuery<NewsItem>({
    queryKey: ["/api/news", id],
    queryFn: async ({ queryKey }) => {
      if (!id) throw new Error("No news item ID provided");
      const res = await fetch(`/api/news/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch news item");
      return res.json();
    },
    enabled: !!id,
  });
}

// Hook for manual refresh
export function useRefreshNews() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/news/refresh", undefined);
      return res.json();
    },
    onSuccess: () => {
      // Invalidate all news queries to refresh the data
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].startsWith("/api/news"),
      });
    },
  });
}

export function useSignals() {
  return useQuery<SignalsResponse>({
    queryKey: ["/api/signals"],
    queryFn: async () => {
      const headers: Record<string, string> = {};
      const mappings = [
        ["fmp", "x-fmp-key"],
        ["fred", "x-fred-key"],
        ["alphaVantage", "x-alpha-vantage-key"],
        ["eia", "x-eia-key"],
        ["cmc", "x-cmc-key"],
        ["newsapi", "x-newsapi-key"],
      ] as const;
      for (const [provider, header] of mappings) {
        const value = getVaultKey(provider);
        if (value) headers[header] = value;
      }
      const res = await fetch("/api/signals", { credentials: "include", headers });
      if (!res.ok) throw new Error("Failed to fetch signals");
      return res.json();
    },
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useIdeas() {
  const groqKey = getVaultKey("groq");
  const credentialRevision = credentialCacheKey(groqKey, getVaultEntry("groq")?.savedAt);
  return useQuery<IdeasHttpResponse>({
    queryKey: ["/api/ideas", credentialRevision],
    queryFn: async ({ signal }) => {
      const headers: Record<string, string> = { ...getClientIdentityHeaders() };
      const currentKey = getVaultKey("groq");
      if (credentialCacheKey(currentKey, getVaultEntry("groq")?.savedAt) !== credentialRevision) {
        throw new Error("The Groq key changed. Refresh ideas to use the current key.");
      }
      if (currentKey) headers["x-groq-key"] = currentKey;
      const res = await fetch("/api/ideas", { credentials: "include", headers, signal });
      if (!res.ok) throw new Error("Failed to fetch ideas");
      return res.json();
    },
    staleTime: 10 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/** A non-secret cache-key revision so TanStack Query does not reuse BYOK results after key rotation. */
function credentialCacheKey(value: string | null, savedAt?: string | null): string {
  if (!value) return "server-credential";
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `byok-${savedAt ?? "unsaved"}-${(hash >>> 0).toString(16)}`;
}

// Hook for auto-refresh with configurable interval
export function useAutoRefresh(initialInterval: RefreshInterval = 60) {
  const [interval, setInterval] = useState<RefreshInterval>(initialInterval);
  const { mutate: refreshNews } = useRefreshNews();

  const refresh = useCallback(() => {
    refreshNews();
  }, [refreshNews]);

  useEffect(() => {
    // Define a function to call refresh
    const handleRefresh = () => {
      refresh();
    };

    // Use global window object to set interval
    // TypeScript in Node.js environment would use NodeJS.Timeout
    // but in browser environment we use number for interval IDs
    const intervalId: number = window.setInterval(handleRefresh, interval * 1000);

    return () => window.clearInterval(intervalId);
  }, [interval, refresh]);

  return { interval, setInterval, refresh };
}
